import { useCallback, useContext, useMemo } from "react"
import { useSelector } from "react-redux"
import { matchPath, useLocation } from "react-router-dom"
import { FeatureFlagContext } from "../../mp-core/FeatureFlagProvider"
import { RootState } from "../../mp-core/redux/store/index"
import { Role } from "../../mp-database/types/Role"
import { User } from "../../mp-database/types/User"
import { Roles } from "../../mp-utils/generic-utils/Enums/index"
import { FeatureFlagName } from "../../mp-utils/generic-utils/feature-flags/types"
import { ROUTES_WITH_DISTRICT_SELECTION_CHECK } from "../constants/nav"
import { FiltersStateContext } from "../providers/FiltersStateProvider"

const MAX_DISTRICTS_COUNT = 10

// Temporary performance test: allow selected reporting pages to load without
// a district while preserving backend scoping and the max-selected guard.
const U_AND_G_ROUTES_WITH_OPTIONAL_DISTRICT = [
  "/reporting/organization",
  "/reporting/organization/states",
  "/reporting/organization/districts",
  "/reporting/organization/admins",
  "/reporting/organization/schools",
  "/reporting/organization/teachers",
  "/reporting/organization/classes",
  "/reporting/organization/students",
  "/reporting/insights/students-usage",
  "/reporting/insights/usage/students",
  "/reporting/insights/teachers-usage",
  "/reporting/insights/usage/teachers",
  "/reporting/insights/activity-log",
  "/reporting/insights/usage/activity-log"
]

const DISABLED_FILTERS_WITHOUT_DISTRICT = [
  "school",
  "teacher",
  "class",
  "student",
  "grade",
  "date",
  "test",
  "userStatus",
  "content",
  "contentType",
  "usageContentType",
  "activity",
  "userRole",
  "adminRole",
  "score"
]

const SCOPE_GATED_ROLES = [
  Roles.Super_Administrator,
  Roles.ACT_Multi_District_Admin,
  Roles.ACT_Admin
]

// District requires a State only in Users and Groups; other sections unaffected.
const USERS_AND_GROUPS_ROUTES_WITH_STATE_CHECK =
  ROUTES_WITH_DISTRICT_SELECTION_CHECK.filter(route =>
    route.startsWith("/reporting/organization")
  )

export const useDistrictSelectionCheck = () => {
  const { state: filters = {} } = useContext(FiltersStateContext) || {}
  const { featureFlags } = useContext(FeatureFlagContext)
  const location = useLocation()
  const { user } = useSelector((state: RootState) => ({
    user: state.users.testUser as User
  }))

  const canBypassScopeGate =
    !!featureFlags?.[FeatureFlagName.reporting_organization_bypass_scope_gate]

  const isUAndGRouteWithOptionalDistrict = useMemo(
    () =>
      Boolean(
        matchPath(location.pathname || "", {
          path: U_AND_G_ROUTES_WITH_OPTIONAL_DISTRICT,
          exact: true
        })
      ),
    [location.pathname]
  )

  const isScopeGatedRole = SCOPE_GATED_ROLES.includes(user.role as Role)

  const isMoreThanMaxDistrictsSelected = useMemo(() => {
    return filters.district?.length > MAX_DISTRICTS_COUNT
  }, [filters.district])

  const isDistrictSelected = useMemo(() => {
    if (
      canBypassScopeGate &&
      isUAndGRouteWithOptionalDistrict &&
      !isMoreThanMaxDistrictsSelected
    ) {
      return true
    }

    if (
      !isScopeGatedRole ||
      (!!filters.district?.length && !isMoreThanMaxDistrictsSelected)
    ) {
      return true
    }

    return false
  }, [
    isScopeGatedRole,
    filters.district,
    isMoreThanMaxDistrictsSelected,
    isUAndGRouteWithOptionalDistrict,
    canBypassScopeGate
  ])

  const isFilterItemDisabledByDistrict = useCallback(
    (filterItem: string, currentLocation: string) => {
      const matchRes = matchPath(currentLocation || "", {
        path: ROUTES_WITH_DISTRICT_SELECTION_CHECK,
        exact: true
      })

      if (
        DISABLED_FILTERS_WITHOUT_DISTRICT.includes(filterItem) &&
        !isDistrictSelected &&
        !!matchRes
      ) {
        return true
      }

      return false
    },
    [isDistrictSelected]
  )

  // District requires a State, like School requires a District. Lifted by the
  // same bypass flag. A District already set (saved view / URL) keeps the chip
  // enabled so it can be edited.
  const isFilterItemDisabledByState = useCallback(
    (filterItem: string, currentLocation: string) => {
      if (
        filterItem !== "district" ||
        !isScopeGatedRole ||
        (canBypassScopeGate && isUAndGRouteWithOptionalDistrict) ||
        !!filters.state?.length ||
        !!filters.district?.length
      ) {
        return false
      }

      return !!matchPath(currentLocation || "", {
        path: USERS_AND_GROUPS_ROUTES_WITH_STATE_CHECK,
        exact: true
      })
    },
    [
      isScopeGatedRole,
      canBypassScopeGate,
      isUAndGRouteWithOptionalDistrict,
      filters.state,
      filters.district
    ]
  )

  return {
    isDistrictSelected,
    isMoreThanMaxDistrictsSelected,
    isFilterItemDisabledByDistrict,
    isFilterItemDisabledByState
  }
}
